import React, { useEffect, useState } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip as RTooltip, YAxis } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import api from '../../lib/api';

// Sparkline of how the org's peer *percentile* for a chain stage has moved
// over recent snapshots. Renders nothing until there are ≥2 comparable points.
export default function PeerBenchmarkTrend({ stage, organizationId, testId, light = false }) {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const q = new URLSearchParams({ stage });
    if (organizationId && organizationId !== 'all') q.set('organization_id', organizationId);
    api.get(`/chain-benchmark/percentile-history?${q.toString()}`)
      .then((r) => { if (!cancelled) setHist(r.data); })
      .catch(() => { if (!cancelled) setHist(null); });
    return () => { cancelled = true; };
  }, [stage, organizationId]);

  const series = hist?.series || [];
  if (series.length < 2) return null;
  const delta = hist.delta ?? 0;
  const up = delta > 0, flat = delta === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const tone = flat ? 'text-slate-400' : up ? 'text-emerald-400' : 'text-rose-400';
  const stroke = flat ? '#94a3b8' : up ? '#34d399' : '#fb7185';
  const first = series[0];
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return d; } };
  const bm = hist.biggest_move;
  const bmText = bm ? `Biggest move ${bm.delta > 0 ? '+' : ''}${bm.delta} pts (${Math.round(bm.value_from)}→${Math.round(bm.value_to)}) around ${fmtDate(bm.to_date)}` : null;
  // Driver attribution — the pillar/component behind the biggest move (or the
  // current weakest component when the window can't be attributed cleanly).
  const drv = hist.driver;
  const driverText = drv
    ? (drv.basis === 'window'
      ? `Driver: ${drv.label} ${drv.delta > 0 ? '+' : ''}${drv.delta} pts over this window`
      : `Likely driver: ${drv.label}${drv.score != null ? ` (weakest now · ${drv.score})` : ''}`)
    : null;
  const fullTitle = [bmText, driverText].filter(Boolean).join('\n') || undefined;

  return (
    <div className={`mt-3 pt-3 border-t ${light ? 'border-slate-200' : 'border-white/5'}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`text-[10px] font-bold uppercase tracking-wide ${light ? 'text-slate-500' : 'text-slate-500'}`}>Percentile trend</span>
        <span className={`text-[11px] font-semibold inline-flex items-center gap-1 ${tone}`} data-testid={testId ? `${testId}-delta` : undefined}>
          <Icon className="h-3 w-3" />{delta > 0 ? '+' : ''}{delta} pts since {fmtDate(first.date)}
        </span>
      </div>
      <div style={{ height: 40 }} title={fullTitle} data-testid={testId ? `${testId}-bigmove` : undefined}>
        <ResponsiveContainer width="100%" height={40}>
          <LineChart data={series} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
            <YAxis hide domain={[0, 100]} />
            <RTooltip
              contentStyle={{ background: '#0b1220', border: '1px solid #22304d', borderRadius: 8, fontSize: 11, padding: '4px 8px' }}
              labelStyle={{ display: 'none' }}
              formatter={(v, _n, p) => [`P${v} · ${fmtDate(p?.payload?.date)}`, 'Percentile']} />
            <Line type="monotone" dataKey="percentile" stroke={stroke} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 3.5 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {driverText && (
        <p className={`mt-1 text-[10px] leading-snug ${light ? 'text-slate-500' : 'text-slate-400'}`} data-testid={testId ? `${testId}-driver` : undefined}>
          {drv.basis === 'window' ? '↳ ' : '↳ Likely: '}
          <span className={`font-semibold ${light ? 'text-slate-700' : 'text-slate-200'}`}>{drv.label}</span>
          {drv.basis === 'window'
            ? <span className={tone}> {drv.delta > 0 ? '+' : ''}{drv.delta} pts</span>
            : (drv.score != null ? <span className="text-slate-500"> · weakest now ({drv.score})</span> : null)}
        </p>
      )}
    </div>
  );
}
